/** @jsx jsx */
import { jsx } from '@emotion/core';
import * as React from 'react';

import colors from '../../../css/colors';
import Button from '../../ui/Button';
import axios from 'axios';
import CustomModal from '../../ui/CustomModal';
import { Checkbox, Icon, Input, Loader, Popup } from 'semantic-ui-react';
import { SettingName, SettingOption } from '../../../types';
import RulesIcon from './RulesIcon';
import { decodeLink } from './CreateDocument';
import qs from 'qs';
import ActionButton from 'components/ui/ActionButton';
import IntegrationSendToClientWarning from './IntegrationSendToClientWarning';
import QuillWysiwyg from 'components/ui/QuillWysiwyg';
import Spinner from 'components/ui/Spinner';
import { toast } from 'react-toastify';
import styled from '@emotion/styled';
import convertToFileDownload from 'components/FileDownloads';
import EmailAddressMultiselect from 'components/ui/EmailAddressMultiselect';
import { getCurrentDateTimeForDateTimePicker } from 'lib/PublicFunctions';
import SendGridErrorModal from 'components/ui/SendGridErrorModal';
import ConfirmActionModal from 'components/ui/ConfirmActionModal';
import LargePdfCompressionPromptModal from 'components/ui/LargePdfCompressionPromptModal';
import PdfCompressionReviewModal from 'components/ui/PdfCompressionReviewModal';
import {
  MAX_COMPRESSION_PASSES,
  MAX_CONCURRENT_COMPRESSION_REQUESTS,
  createCompressionSessionId,
  createPdfCompressionPreview,
  cleanupPdfCompressionArtifacts,
  getCompressionFailureStatus,
  getPdfSizes,
  runWithConcurrency,
  savePdfCompressionArtifacts,
} from 'lib/pdfCompressionApi';
import { selectCompressionCandidates } from 'lib/pdfCompressionFlow';

const madisonClients = [42, 54, 156, 183, 192, 210, 359, 575, 600, 601, 602];
const judicialClients = [41, 860];
const stewartClientId = 745;

const titleStyles = {
  backgroundColor: colors.greyOrder,
  height: 72,
  width: 463,
  borderRadius: 5,
  display: 'flex',
  alignItems: 'center',
  paddingLeft: 22,
  fontWeight: 'bold',
  fontSize: 18,
};
const documentStyles = {
  backgroundColor: colors.grey,
  borderRadius: 5,
  width: 320,
  height: 48,
  marginTop: 16,
  display: 'flex',
  alignItems: 'center',
  paddingLeft: 16,
  ':hover': {
    color: '#ff5252',
  },
};

const InputLabel = styled.span`
  font-size: 14px;
  color: ${props => (props.disabled ? 'grey' : '#444444')};
`;

const getSelectedEmailValues = selections =>
  selections
    .filter(selection => selection.value && selection.value.includes('@'))
    .map(selection => selection.value);

const getSendToClientDocumentName = document =>
  document.documentType ??
  (document.fileName.includes('billingDoc.pdf') ? 'BillingDocument' : 'Order Correspondence');

const getSelectedDocumentKey = documents =>
  [...new Set((documents || []).map(document => document.id))].sort((a, b) => a - b).join(',');

const getSendToClientErrorMessage = (responseData, fallbackMessage) =>
  (typeof responseData === 'string' ? responseData : responseData?.errorMessage ?? responseData?.message) ||
  fallbackMessage;

const isDefaultIntegrationAddress = email => {
  const normalizedEmail = email?.trim().toLowerCase();
  return normalizedEmail?.includes('trackerpro') || normalizedEmail === 'vendors@accutitle.com';
};

const onlyDefaultIntegrationAddressSelected = emails =>
  emails.length === 1 && isDefaultIntegrationAddress(emails[0]);

const getIntegrationAddressWarningMessage = email =>
  `Please confirm that you would like to send this email to the following email address:\n\n${email}`;

const CompressionCheckStatus = styled.div`
  align-items: center;
  color: #4d4e4d;
  display: flex;
  font-size: 14px;
  gap: 8px;
  margin-top: 16px;
`;

const CompressionActivity = styled.div`
  align-items: center;
  color: #4d4e4d;
  display: flex;
  flex-direction: column;
  font-size: 14px;
  gap: 8px;
  margin-top: 16px;
  text-align: center;
`;

const CompressionProgressTrack = styled.div`
  background-color: #e5e5ea;
  border-radius: 999px;
  height: 8px;
  margin: 10px auto 0;
  overflow: hidden;
  width: 100%;
`;

const IndeterminateCompressionProgress = styled.div`
  animation: compression-progress 1.4s ease-in-out infinite;
  background-color: #ff5252;
  border-radius: 999px;
  height: 100%;
  width: 35%;

  @keyframes compression-progress {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(285%);
    }
  }
`;

export default class SendToClient extends React.Component {
  componentMounted = false;
  compressionDocumentSelectionKey = null;
  compressionSizeCheckPromise = null;

  state = {
    contacts: [],
    client: {},
    sendStatus: false,
    error: false,
    sendEmail: true,
    sendViaIntegration: false,
    trackerProError: null,
    riversideUploadmessage: '',
    orderLocation: 0,
    searchIds: [],
    selectedContacts: [],
    selectedCcEmails: [],
    selectedBccEmails: [],
    subject: '',
    emailBody: '',
    addToBodyIfCompleted: '',
    loading: false,
    sendGridError: false,
    possibleAttachmentSizeFailure: false,
    canTryCompressionAfterFailure: true,
    failureCompressionCandidates: null,
    modalErrorMessage: '',
    failedEmailBase64: '',
    failedEmailFileName: '',
    subjectError: false,
    judicialContinError: false,
    scheduleSend: false,
    scheduleSendMin: '',
    scheduleSendDateTime: '',
    hasIntegration: false,
    isTrackerPro: false,
    isSnap: false,
    isAtr: false,
    isAccutitle: false,
    isMadisonNj: false,
    isStewart: false,
    isJudicial: false,
    judicialSendContinsTo: [],
    hasContin: false,
    emailJudicialContins: true,
    integrationAddressWarningOpen: false,
    integrationAddressWarningEmail: '',
    compressionFlow: {
      phase: 'idle',
      sessionId: createCompressionSessionId(),
      afterFailure: false,
      documents: [],
      previews: [],
      progress: { completed: 0, total: 0 },
      outcomes: {},
      passCount: 0,
      replacements: [],
      sizeCheckComplete: false,
    },
  };

  componentWillUnmount() {
    this.componentMounted = false;
    this.cleanupCompressionArtifacts(this.state.compressionFlow.previews.map(preview => preview.artifactId));
  }

  async componentDidMount() {
    this.componentMounted = true;
    this.compressionDocumentSelectionKey = getSelectedDocumentKey(this.props.selectedDocuments);
    let continReceiver = [];
    const searchIds = [
      ...new Set(
        this.props.selectedDocuments.reduce(
          (ids, document) => ids.concat((document.searches || []).map(search => search.id)),
          []
        )
      ),
    ];
    const queryParams = qs.stringify({ searchIds });
    this.setState({ searchIds, loading: true });
    const { data: searchInfo } =
      searchIds.length > 0
        ? await axios.get(`/api/searches/GetSendToClientSearchInfo?${queryParams}`)
        : { data: [] };
    const includesContin = searchInfo.some(search => search.isContinOrUpdate);
    const { data: continReceivers } = includesContin
      ? await axios.get(`/api/searches/getContinReceiver?${queryParams}`)
      : { data: [] };
    if ((continReceivers || []).length > 0) {
      continReceiver = continReceiver.concat(
        continReceivers.map(continReceiverData => ({
          ...continReceiverData,
          selected: true,
          continReceiver: true,
          fromOrderEntry: continReceiverData.fromOrderEntry,
          email: continReceiverData.emailAddress,
        }))
      );
    }

    const { orderId } = this.props;
    const [contactsResponse, clientResponse] = await Promise.all([
      axios.get(`/api/orders/getClientContacts/${orderId}`),
      axios.get(`/api/orders/getClient/${orderId}`),
    ]);
    const { data: orderlocation } = await axios.get(`/api/orders/getOrderLocation/${orderId}`);
    this.setState({ orderLocation: orderlocation });
    const { data } = await axios.get(
      `/api/clients/GetClientSetting/${clientResponse.data.id}?setting=${SettingName.sendviaemail}`
    );
    const sendViaEmailClient = data === SettingOption.yes;
    const allDocsTp = this.props.selectedDocuments.every(
      d => d.searches?.length > 0 && d.searches.every(s => s.trackerproId)
    );
    const trackerPro = allDocsTp || clientResponse.data.trackerProId > 0;
    const snap = Boolean(clientResponse.data.snapClientName);
    const madisonNj = madisonClients.includes(clientResponse.data.id);
    const stewart = clientResponse.data.id === stewartClientId;
    const judicial = judicialClients.includes(clientResponse.data.id);
    let accutitle = false;
    let atr = false;
    if (!trackerPro && !snap && !madisonNj && !stewart && !judicial) {
      const { data: isAtrOrder } = await axios.get(`/api/documents/getIfAtrOrder/${orderId}`);
      atr = isAtrOrder;
    }
    if (!atr && !trackerPro && !snap && !madisonNj && !stewart && !judicial) {
      const { data: isAccutitleClient } = await axios.get(
        `/api/documents/getIfAccutitleClient/${clientResponse.data?.id}`
      );
      accutitle = isAccutitleClient;
    }

    const defaultSendViaIntegration = trackerPro || snap || (madisonNj && orderlocation === 3) || accutitle || atr || stewart || judicial;
    const defaultSendViaEmail = sendViaEmailClient || !defaultSendViaIntegration || (madisonNj && orderlocation !== 3);
    const getSubjectQueryParams = qs.stringify({ searchIds, clientId: clientResponse.data.id, orderId });
    const { data: subjectAndBody } =
      searchIds.length > 0
        ? await axios.get(`/api/documents/getEmailSubjectAndBody?${getSubjectQueryParams}`)
        : { data: null };
    const allSearchesAreContins =
      searchIds.length > 0 &&
      searchInfo.length === searchIds.length &&
      searchInfo.every(search => search.isContinOrUpdate);
    const continDefaultField =
      allSearchesAreContins && searchInfo.every(search => search.searchCategoryId === 2)
        ? 'defaultTaxContinContact'
        : allSearchesAreContins && searchInfo.every(search => search.searchCategoryId === 3)
        ? 'defaultTitleContinContact'
        : null;
    const defaultContactField =
      continDefaultField && contactsResponse.data.some(c => c[continDefaultField] && c.email?.trim())
        ? continDefaultField
        : 'defaultContact';
    const contactsNotInContinReceiver = contactsResponse.data.filter(
      c => !continReceiver.find(cr => cr.email === c.email)
    );

    const contactsList = continReceiver.concat(
      contactsNotInContinReceiver.map(c => {
        return {
          ...c,
          selected:
            defaultContactField === 'defaultContact'
              ? c.defaultContact
              : Boolean(c[defaultContactField] && c.email?.trim()),
        };
      })
    );
    var selectedOptions = contactsList
      .filter(c => c.selected)
      .map(c => ({
        value: c.email,
        label: `${c.email}${
          c.fromOrderEntry ? ' (Order Contact)' : c.continReceiver ? ' (Contin Receiver)' : ''
        }`,
      }));
    this.setState({
      contacts: contactsList.map(c => ({
        value: c.email,
        label: `${c.email}${
          c.fromOrderEntry ? ' (Order Contact)' : c.continReceiver ? ' (Contin Receiver)' : ''
        }`,
      })),
      client: {
        ...clientResponse.data,
        selected:
          clientResponse.data.defaultContact && continReceiver.filter(cr => !cr.fromOrderEntry).length < 1,
      },
      sendEmail: defaultSendViaEmail,
      sendViaIntegration: defaultSendViaIntegration,
      hasIntegration: defaultSendViaIntegration,
      isTrackerPro: trackerPro,
      isSnap: snap,
      isAtr: atr,
      isMadisonNj: madisonNj,
      isAccutitle: accutitle,
      isStewart: stewart,
      isJudicial: judicial,
      selectedContacts: selectedOptions,
      judicialSendContinsTo: selectedOptions,
      subject: subjectAndBody?.subject ?? '',
      emailBody: subjectAndBody?.body ?? '',
      addToBodyIfCompleted: subjectAndBody?.completedAddToBody ?? '',
      loading: true,
      hasContin: includesContin,
    });

    if (defaultSendViaEmail) {
      this.openCompressionPrompt();
    }

    this.setState({ loading: false });
  }

  async componentDidUpdate(prevProps, prevState) {
    const currentDocumentSelectionKey = getSelectedDocumentKey(this.props.selectedDocuments);
    if (
      this.compressionDocumentSelectionKey !== null &&
      currentDocumentSelectionKey !== this.compressionDocumentSelectionKey
    ) {
      this.compressionDocumentSelectionKey = currentDocumentSelectionKey;
      this.compressionSizeCheckPromise = null;
      this.cleanupCompressionArtifacts(
        this.state.compressionFlow.previews.map(preview => preview.artifactId)
      );
      this.setState({
        compressionFlow: {
          ...this.state.compressionFlow,
          phase: 'idle',
          sessionId: createCompressionSessionId(),
          afterFailure: false,
          documents: [],
          previews: [],
          progress: { completed: 0, total: 0 },
          outcomes: {},
          passCount: 0,
          replacements: [],
          sizeCheckComplete: false,
        },
      });
      return;
    }

    const {
      selectedContacts,
      selectedCcEmails,
      selectedBccEmails,
      emailBody,
      addToBodyIfCompleted,
    } = this.state;
    if (
      selectedContacts.length != prevState.selectedContacts.length ||
      selectedCcEmails.length != prevState.selectedCcEmails.length ||
      selectedBccEmails.length != prevState.selectedBccEmails.length
    ) {
      if (
        selectedContacts.find(s => s.value.toLowerCase() == 'hudsoncompletedsearches@hudsonsearch.com') ||
        selectedCcEmails.find(s => s.value.toLowerCase() == 'hudsoncompletedsearches@hudsonsearch.com') ||
        selectedBccEmails.find(s => s.value.toLowerCase() == 'hudsoncompletedsearches@hudsonsearch.com')
      ) {
        if (!emailBody.includes(addToBodyIfCompleted.replace(/<\/?[^>]+(>|$)/g, ''))) {
          const editedEmailBody = `${emailBody}${addToBodyIfCompleted}`;
          this.setState({ emailBody: editedEmailBody });
        }
      } else {
        if (emailBody.includes(addToBodyIfCompleted.replace(/<\/?[^>]+(>|$)/g, ''))) {
          this.setState({
            emailBody: emailBody.replace(addToBodyIfCompleted.replace(/<\/?[^>]+(>|$)/g, ''), ''),
          });
        }
      }
    }
  }

  handleSendClick = () => {
    this.submitSendToClient();
  };

  updateCompressionFlow = update => {
    if (!this.componentMounted) {
      return;
    }

    this.setState(state => ({
      compressionFlow: {
        ...state.compressionFlow,
        ...(typeof update === 'function' ? update(state.compressionFlow) : update),
      },
    }));
  };

  getCompressionCandidates = async ({ fallbackForFailure = false } = {}) => {
    const selectedDocuments = [
      ...new Map(this.props.selectedDocuments.map(document => [document.id, document])).values(),
    ];
    if (selectedDocuments.length === 0) {
      return [];
    }

    const flow = this.state.compressionFlow;
    const replacements = flow.replacements;
    const replacementByOriginal = replacements.reduce((result, replacement) => {
      result[replacement.originalDocumentId] = replacement.compressedDocumentId;
      return result;
    }, {});
    const effectiveDocumentIds = selectedDocuments.map(
      document => replacementByOriginal[document.id] || document.id
    );
    const data = await getPdfSizes(effectiveDocumentIds, this.props.orderId);
    return selectCompressionCandidates({
      selectedDocuments,
      sizeDocuments: data.documents,
      replacements,
      outcomes: flow.outcomes,
      fallbackForFailure,
    });
  };

  openCompressionPrompt = () => {
    if (this.compressionSizeCheckPromise) {
      return this.compressionSizeCheckPromise;
    }

    this.updateCompressionFlow({ phase: 'checking-size' });

    this.compressionSizeCheckPromise = (async () => {
      try {
        const compressionDocuments = await this.getCompressionCandidates();
        if (compressionDocuments.length === 0 || !this.componentMounted) {
          return false;
        }

        this.updateCompressionFlow({
          phase: 'prompt',
          documents: compressionDocuments,
          afterFailure: false,
        });
        return true;
      } catch (exception) {
        toast.error(
          exception?.response?.data?.message ||
            'The selected files could not be checked for size. The original files will be sent.'
        );
        return false;
      } finally {
        if (this.componentMounted) {
          this.updateCompressionFlow(flow => ({
            ...(flow.phase === 'checking-size' ? { phase: 'idle' } : {}),
            sizeCheckComplete: true,
          }));
        }
        this.compressionSizeCheckPromise = null;
      }
    })();

    return this.compressionSizeCheckPromise;
  };

  compressDocument = async document => {
    const data = await createPdfCompressionPreview({
      documentId: document.documentId,
      orderId: this.props.orderId,
      compressionSessionId: this.state.compressionFlow.sessionId,
    });
    return {
      artifactId: data.artifactId,
      documentId: document.documentId,
      fileName: data.originalFileName,
      originalDocumentUrl: data.originalDocumentUrl || `/api/documents/getDocument/${document.documentId}`,
      compressedDocumentUrl: data.compressedDocumentUrl,
      originalSizeBytes: data.originalSizeBytes,
      compressedSizeBytes: data.compressedSizeBytes,
      reductionPercentage: data.reductionPercentage,
      isSmaller: data.isSmaller,
    };
  };

  startCompression = async ({ documents = null, fallbackForFailure = false, afterFailure = false } = {}) => {
    const currentFlow = this.state.compressionFlow;
    if (currentFlow.passCount >= MAX_COMPRESSION_PASSES) {
      toast.info(
        'Compression has already been attempted for these attachments. Please download the email to send manually.'
      );
      return false;
    }

    this.setState({
      loading: true,
    });
    this.updateCompressionFlow({
      phase: 'compressing',
      afterFailure,
      previews: [],
      progress: { completed: 0, total: 0 },
    });

    try {
      const compressionDocuments = documents || (await this.getCompressionCandidates({ fallbackForFailure }));
      if (compressionDocuments.length === 0) {
        if (!fallbackForFailure) {
          toast.info('None of the selected PDFs are 15 MB or larger.');
        }
        this.updateCompressionFlow({ phase: 'idle', documents: [] });
        return false;
      }

      if (!this.componentMounted) {
        return false;
      }

      const passCount = this.state.compressionFlow.passCount + 1;
      this.updateCompressionFlow({
        documents: compressionDocuments,
        passCount,
        progress: { completed: 0, total: compressionDocuments.length },
      });

      const results = await runWithConcurrency(
        compressionDocuments,
        document => this.compressDocument(document),
        MAX_CONCURRENT_COMPRESSION_REQUESTS,
        () =>
          this.updateCompressionFlow(flow => ({
            progress: {
              ...flow.progress,
              completed: Math.min(flow.progress.completed + 1, flow.progress.total),
            },
          }))
      );
      const previews = results.filter(result => result.value).map(result => result.value);
      const failedResults = results.filter(result => result.exception);
      const outcomes = { ...this.state.compressionFlow.outcomes };
      compressionDocuments.forEach((document, index) => {
        const result = results[index];
        outcomes[document.documentId] = result.exception
          ? { status: getCompressionFailureStatus(result.exception), passCount }
          : { status: 'preview-ready', passCount };
      });

      if (failedResults.length > 0) {
        toast.warn(
          `${failedResults.length} PDF${
            failedResults.length === 1 ? '' : 's'
          } could not be compressed. The available results are shown for review.`
        );
      }
      if (previews.length === 0) {
        toast.error('The selected PDFs could not be compressed. The original files are unchanged.');
        this.updateCompressionFlow({ phase: 'idle', outcomes });
        return false;
      }

      if (!this.componentMounted) {
        return false;
      }

      this.updateCompressionFlow({
        phase: 'review',
        documents: compressionDocuments,
        previews,
        outcomes,
      });
      return true;
    } catch (exception) {
      toast.error(
        exception?.response?.data?.message || 'The selected files could not be checked for compression.'
      );
      this.updateCompressionFlow({ phase: 'idle', documents: [] });
      return false;
    } finally {
      if (this.componentMounted) {
        this.setState({ loading: false });
      }
    }
  };

  submitSendToClient = async ({
    bypassIntegrationAddressWarning = false,
    skipCompressionCheck = false,
  } = {}) => {
    if (this.state.sendStatus) {
      return;
    }

    const {
      client,
      selectedContacts,
      selectedCcEmails,
      selectedBccEmails,
      subject,
      emailBody,
      sendViaIntegration,
      isTrackerPro,
      isAccutitle,
      isSnap,
      isAtr,
      isMadisonNj,
      isStewart,
      isJudicial,
      sendEmail,
      scheduleSend,
      scheduleSendDateTime,
      judicialSendContinsTo,
      hasContin,
      emailJudicialContins,
    } = this.state;
    const { selectedDocuments } = this.props;
    const error = !client.selected && !selectedContacts?.length && (!sendViaIntegration || sendEmail);
    const emptySubject = (!sendViaIntegration || sendEmail) && !subject?.trim();
    const judicialError =
      sendViaIntegration &&
      isJudicial &&
      hasContin &&
      emailJudicialContins &&
      !(judicialSendContinsTo?.length > 0);
    if (error || emptySubject || judicialError) {
      this.setState({
        error,
        subjectError: emptySubject,
        judicialContinError: judicialError,
        loading: false,
      });
      return;
    }

    const emails = (client.selected ? [client.email] : []).concat(getSelectedEmailValues(selectedContacts));
    const shouldShowIntegrationAddressWarning =
      sendEmail && onlyDefaultIntegrationAddressSelected(emails) && !bypassIntegrationAddressWarning;
    if (shouldShowIntegrationAddressWarning) {
      this.setState({
        integrationAddressWarningOpen: true,
        integrationAddressWarningEmail: emails[0],
      });
      return;
    }

    if (sendEmail && !skipCompressionCheck && !this.state.compressionFlow.sizeCheckComplete) {
      const compressionPromptOpened = await this.openCompressionPrompt();
      if (compressionPromptOpened) {
        return;
      }
    }

    this.setState({ sendStatus: true, loading: true });

    const postObj = {
      orderId: this.props.orderId,
      documentIds: selectedDocuments.map(d => ({
        id: d.id,
        name: getSendToClientDocumentName(d),
      })),
      emailAddresses: emails,
      clientId: client.id,
      sendEmail,
      sendViaIntegration: Boolean(sendViaIntegration),
      isTrackerPro,
      isAccutitle,
      isSnap,
      isAtr,
      isMadisonNj,
      isStewart,
      isJudicial,
      ccEmailAddresses: getSelectedEmailValues(selectedCcEmails),
      bccEmailAddresses: getSelectedEmailValues(selectedBccEmails),
      subject,
      emailBody,
      scheduleSend,
      scheduleSendDateTime,
      judicialSendContinsTo: getSelectedEmailValues(judicialSendContinsTo),
      emailJudicialContins,
      compressedDocumentReplacements: this.state.compressionFlow.replacements,
    };

    try {
      const { data } = await axios.post(`/api/documents/sendToClient`, postObj);
      await this.handleSendToClientResponse(data);
    } catch (e) {
      const responseData = e?.response?.data;
      const modalErrorMessage = getSendToClientErrorMessage(
        responseData,
        'Something went wrong while sending to client. The email may not have been sent.'
      );

      await this.handleSendToClientError(responseData, modalErrorMessage);
    } finally {
      this.setState({ sendStatus: false, loading: false });
    }
  };

  handleIntegrationAddressWarningClose = () => {
    this.setState({
      integrationAddressWarningOpen: false,
      integrationAddressWarningEmail: '',
    });
  };

  handleIntegrationAddressWarningContinue = () => {
    this.setState(
      {
        integrationAddressWarningOpen: false,
      },
      () => this.submitSendToClient({ bypassIntegrationAddressWarning: true })
    );
  };

  handleCompressionPromptClose = () => {
    this.updateCompressionFlow({ phase: 'idle', documents: [] });
  };

  handleCompressionPromptTry = () => {
    const documents = this.state.compressionFlow.documents;
    this.startCompression({ documents });
  };

  handleCompressionPromptSendOriginal = () => {
    this.updateCompressionFlow({ phase: 'idle', documents: [] });
  };

  cleanupCompressionArtifacts = artifactIds => {
    const distinctArtifactIds = [...new Set(artifactIds.filter(Boolean))];
    if (distinctArtifactIds.length === 0) {
      return;
    }

    cleanupPdfCompressionArtifacts({
      compressionSessionId: this.state.compressionFlow.sessionId,
      artifactIds: distinctArtifactIds,
    }).catch(() => {});
  };

  handleCompressionConfirm = async artifactIds => {
    const data = await savePdfCompressionArtifacts({
      orderId: this.props.orderId,
      compressionSessionId: this.state.compressionFlow.sessionId,
      artifactIds,
    });
    const replacements = (data.replacements || [])
      .filter(replacement => replacement.compressedDocumentId)
      .map(replacement => ({
        originalDocumentId: replacement.originalDocumentId,
        compressedDocumentId: replacement.compressedDocumentId,
      }));

    const flow = this.state.compressionFlow;
    const approvedArtifacts = new Set(artifactIds);
    const outcomes = { ...flow.outcomes };
    flow.previews.forEach(preview => {
      if (approvedArtifacts.has(preview.artifactId) && preview.isSmaller) {
        outcomes[preview.documentId] = { status: 'success', passCount: flow.passCount };
      } else if (preview.isSmaller === false) {
        outcomes[preview.documentId] = { status: 'no-reduction', passCount: flow.passCount };
      }
    });
    const mergedReplacements = [
      ...flow.replacements.filter(
        replacement => !replacements.some(item => item.originalDocumentId === replacement.originalDocumentId)
      ),
      ...replacements,
    ];
    const shouldRetrySend = flow.afterFailure;
    this.cleanupCompressionArtifacts(flow.previews.map(preview => preview.artifactId));
    this.setState(
      {
        compressionFlow: {
          ...flow,
          phase: 'idle',
          afterFailure: false,
          documents: [],
          previews: [],
          outcomes,
          replacements: mergedReplacements,
        },
      },
      () => {
        if (replacements.length === 0) {
          toast.info('The compression did not produce a smaller PDF. The original files were not replaced.');
        }
        if (shouldRetrySend) {
          this.submitSendToClient({ skipCompressionCheck: true });
        }
      }
    );
  };

  handleCompressionSendOriginal = () => {
    const flow = this.state.compressionFlow;
    this.cleanupCompressionArtifacts(flow.previews.map(preview => preview.artifactId));
    const outcomes = { ...flow.outcomes };
    flow.previews.forEach(preview => {
      if (outcomes[preview.documentId]?.status === 'preview-ready') {
        outcomes[preview.documentId] = { status: 'cancelled', passCount: flow.passCount };
      }
    });
    this.updateCompressionFlow({
      phase: 'idle',
      afterFailure: false,
      documents: [],
      previews: [],
      outcomes,
    });
  };

  handleCompressionClose = () => {
    this.handleCompressionSendOriginal();
  };

  handleCompressAfterFailure = async () => {
    const started = await this.startCompression({
      documents: this.state.failureCompressionCandidates,
      fallbackForFailure: true,
      afterFailure: true,
    });
    if (!started) {
      this.handleDownloadFailedEmail();
    }
  };

  handleEmailCheck = (e, { checked }) => {
    this.setState({ sendEmail: checked });

    if (checked && !this.state.compressionFlow.sizeCheckComplete) {
      this.openCompressionPrompt();
    }
  };

  handleIntegrationCheck = (e, { checked }) => {
    const sendEmail = checked ? this.state.sendEmail : true;
    this.setState({ sendEmail, sendViaIntegration: checked });

    if (sendEmail && !this.state.compressionFlow.sizeCheckComplete) {
      this.openCompressionPrompt();
    }
  };
  handleScheduleSendCheck = (e, { checked }) => {
    this.setState({ scheduleSend: checked });
    if (this.state.scheduleSendDateTime === '') {
      const minDateTime = getCurrentDateTimeForDateTimePicker();
      this.setState({ scheduleSendDateTime: minDateTime, scheduleSendMin: minDateTime });
    }
  };
  openFormClick = async (propertyId, link) => {
    const computedLink = await decodeLink(propertyId, link);
    window.open(`${computedLink}?isEdit=true`, '_self');
  };

  handleSendToClientError = async (data, fallbackMessage = '') => {
    const possibleAttachmentSizeFailure = !!data?.possibleAttachmentSizeFailure;
    let failureCompressionCandidates = null;
    let canTryCompressionAfterFailure = true;

    if (possibleAttachmentSizeFailure) {
      try {
        failureCompressionCandidates = await this.getCompressionCandidates({ fallbackForFailure: true });
        canTryCompressionAfterFailure = failureCompressionCandidates.length > 0;
      } catch (exception) {
        // Keep the compression option available if the pre-check cannot be completed.
        canTryCompressionAfterFailure = true;
      }
    }

    this.setState({
      modalErrorMessage: data?.errorMessage || data?.message || fallbackMessage,
      possibleAttachmentSizeFailure,
      canTryCompressionAfterFailure,
      failureCompressionCandidates,
      failedEmailBase64: possibleAttachmentSizeFailure ? data?.base64 || '' : '',
      failedEmailFileName: possibleAttachmentSizeFailure ? data?.fileName || '' : '',
      sendGridError: true,
    });
    if (data?.base64 && data?.fileName && !possibleAttachmentSizeFailure) {
      convertToFileDownload(data.base64, data.fileName);
    }
  };

  handleDownloadFailedEmail = () => {
    if (this.state.failedEmailBase64 && this.state.failedEmailFileName) {
      convertToFileDownload(this.state.failedEmailBase64, this.state.failedEmailFileName);
    }
  };

  handleSendToClientResponse = async data => {
    if (data.sendGridSuccess === false) {
      await this.handleSendToClientError(data);
      return;
    }

    if (data.isTrackerProOrder) {
      this.handleTrackerProResponse(data);
      return;
    }

    if (data.sendGridSuccess) {
      this.props.clearSelectedDocs();
      this.showSuccessToast({
        message: data.message ?? data.errorMessage,
        scheduled: data.scheduled,
        hasIntegrationError: data.hasIntegrationError === true,
      });
      this.props.onClose();
      return;
    }

    await this.handleSendToClientError(data);
  };

  handleTrackerProResponse = data => {
    if (
      data.tpResults.some(
        i => !i.value.item1 || i.value.item2.includes('Failed to save the document through the integration')
      )
    ) {
      this.setState({ trackerProError: true });
      return;
    }

    if (data.tpResults.every(i => i.value.item1)) {
      this.props.clearSelectedDocs();
      this.setState({ trackerProError: false });
      this.showSuccessToast({ message: data.message ?? data.errorMessage, scheduled: data.scheduled });
      this.props.onClose();
    }
  };

  handleSetSendGridError = sendGridError => {
    this.setState({
      sendGridError,
      ...(sendGridError
        ? {}
        : {
            possibleAttachmentSizeFailure: false,
            canTryCompressionAfterFailure: true,
            failureCompressionCandidates: null,
            modalErrorMessage: '',
            failedEmailBase64: '',
            failedEmailFileName: '',
          }),
    });
  };

  showSuccessToast = ({ message, scheduled, hasIntegrationError = false }) => {
    toast(
      message?.length > 0
        ? message
        : this.state.sendViaIntegration
        ? `Successfully ${scheduled ? 'Scheduled' : 'Sent'} via Integration and Email`
        : `Email ${scheduled ? 'Scheduled' : 'Sent'} Successfully`,
      {
        hideProgressBar: true,
        position: 'top-center',
        type: hasIntegrationError ? 'warning' : 'success',
        autoClose: 6000,
        theme: 'colored',
        style: { backgroundColor: hasIntegrationError ? '#F57C00' : '#4CAF50' },
      }
    );
  };

  render() {
    const {
      contacts,
      client,
      sendStatus,
      error,
      sendEmail,
      selectedContacts,
      selectedCcEmails,
      selectedBccEmails,
      subject,
      emailBody,
      loading,
      subjectError,
      judicialContinError,
    } = this.state;
    const { onClose, selectedDocuments } = this.props;

    return (
      <React.Fragment>
        <CustomModal onClose={onClose} notCloseOverlay={true} moreStyles={{ maxWidth: 542 }}>
          {loading && <Spinner />}
          <div style={{ display: 'flex', marginTop: -24, marginBottom: 14 }}>
            <RulesIcon
              clientId={client.id}
              orderId={this.props.orderId}
              padding="6px 6px 0px 5px"
              locationTypeId={this.props.locationTypeId}
              municipalityIds={this.props.municipalityIds}
              subMunicipalityIds={this.props.subMunicipalityIds}
              countyIds={this.props.countyIds}
            />
            <Popup
              content="View Order"
              on={'hover'}
              position="top center"
              mouseLeaveDelay={50}
              style={{ height: 'max-content' }}
              wide="very"
              hoverable={true}
              trigger={
                <span
                  style={{
                    height: 45,
                    border: '1px solid #ff5252',
                    borderRadius: 5,
                    color: '#ff5252',
                    marginLeft: 10,
                  }}
                >
                  <ActionButton
                    backgroundColor="white"
                    semanticIconName="file alternate outline"
                    onClick={() => this.props.handleViewOrderClick(this.state.client)}
                  />
                </span>
              }
            />
          </div>
          <div css={titleStyles}>Send to client - {client.name}</div>

          {this.state.compressionFlow.phase === 'checking-size' && (
            <CompressionCheckStatus>
              <Loader active inline size="small" />
              <span>Checking selected PDFs for size before enabling Send…</span>
            </CompressionCheckStatus>
          )}

          {this.state.compressionFlow.phase === 'compressing' && (
            <CompressionActivity>
              <div>
                {this.state.compressionFlow.progress.total === 0
                  ? 'Checking selected PDFs before compression begins…'
                  : this.state.compressionFlow.progress.total === 1
                  ? 'Compressing the selected PDF…'
                  : `Compressing selected PDFs (${this.state.compressionFlow.progress.completed} of ${this.state.compressionFlow.progress.total} complete)…`}
              </div>
              <CompressionProgressTrack>
                {this.state.compressionFlow.progress.total === 1 ? (
                  <IndeterminateCompressionProgress />
                ) : this.state.compressionFlow.progress.total > 1 ? (
                  <div
                    style={{
                      backgroundColor: '#ff5252',
                      borderRadius: 999,
                      height: '100%',
                      transition: 'width 0.25s ease',
                      width: `${Math.round(
                        (this.state.compressionFlow.progress.completed /
                          this.state.compressionFlow.progress.total) *
                          100
                      )}%`,
                    }}
                  />
                ) : null}
              </CompressionProgressTrack>
            </CompressionActivity>
          )}

          {this.state.hasIntegration && (
            <div>
              <div style={{ marginTop: 16 }}>
                <Checkbox
                  checked={this.state.sendEmail}
                  onChange={this.handleEmailCheck}
                  label="Send Email "
                />
              </div>
              <div style={{ marginTop: 16, marginBottom: 16 }}>
                <Checkbox
                  checked={this.state.sendViaIntegration}
                  onChange={this.handleIntegrationCheck}
                  label={`Send Via ${
                    this.state.isStewart
                      ? 'Stewart'
                      : this.state.isJudicial
                      ? 'Judicial'
                      : this.state.isTrackerPro
                      ? 'TrackerPro'
                      : this.state.isAccutitle
                      ? 'Accutitle'
                      : this.state.isSnap
                      ? 'Snap'
                      : this.state.isAtr
                      ? 'ATR'
                      : this.state.isMadisonNj
                      ? 'Madison'
                      : ''
                  } Integration`}
                />
                {this.state.isJudicial && this.state.hasContin && (
                  <div style={{ marginTop: 16 }}>
                    <Checkbox
                      checked={this.state.sendViaIntegration && this.state.emailJudicialContins}
                      onChange={(e, { checked }) => this.setState({ emailJudicialContins: checked })}
                      label="Send Separate Email with Contins"
                    />

                    <EmailAddressMultiselect
                      title="Send Contins To:"
                      contactsList={contacts}
                      selectedContactsList={this.state.judicialSendContinsTo}
                      onChangeSelections={selections =>
                        this.setState({ judicialSendContinsTo: selections ?? [] })
                      }
                      width="75%"
                      placeholder="Select email addresses"
                      disabled={!this.state.sendViaIntegration || !this.state.emailJudicialContins}
                    />
                  </div>
                )}
              </div>
              <IntegrationSendToClientWarning
                client={this.state.client}
                searchIds={this.state.searchIds}
                isAccutitle={this.state.isAccutitle}
                isAtr={this.state.isAtr}
              />
              <hr />
            </div>
          )}
          <EmailAddressMultiselect
            title={'Send to:'}
            contactsList={contacts.filter(
              c => !selectedCcEmails.concat(selectedBccEmails).find(s => s.value === c.value)
            )}
            onChangeSelections={selections => this.setState({ selectedContacts: selections ?? [] })}
            selectedContactsList={selectedContacts}
            placeholder={'Select Contacts'}
            disabled={!sendEmail}
          />
          <EmailAddressMultiselect
            title={'CC:'}
            contactsList={contacts.filter(
              c => !selectedContacts.concat(selectedBccEmails).find(s => s.value === c.value)
            )}
            onChangeSelections={selections => this.setState({ selectedCcEmails: selections ?? [] })}
            selectedContactsList={selectedCcEmails}
            placeholder={'Select CC Emails'}
            disabled={!sendEmail}
          />
          <EmailAddressMultiselect
            title={'BCC:'}
            contactsList={contacts.filter(
              c => !selectedContacts.concat(selectedCcEmails).find(s => s.value === c.value)
            )}
            onChangeSelections={selections => this.setState({ selectedBccEmails: selections ?? [] })}
            selectedContactsList={selectedBccEmails}
            placeholder={'Select BCC Emails'}
            disabled={!sendEmail}
          />
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              marginTop: 38,
              marginBottom: 8,
            }}
          >
            <InputLabel disabled={!sendEmail} css={{ marginTop: 0, alignContent: 'center' }}>
              Subject:
            </InputLabel>
            <Input
              basic
              value={subject}
              onChange={e => this.setState({ subject: e.target.value })}
              style={{ width: '86%' }}
              disabled={!sendEmail}
            />
          </div>
          <div>
            <InputLabel disabled={!sendEmail}>Email Body:</InputLabel>
            <QuillWysiwyg
              value={emailBody}
              maxheight={200}
              onChange={value => {
                this.setState({ emailBody: value });
              }}
              marginBottom={8}
              borderRadius={5}
              boxShadow="rgba(0, 0, 0, 0.1) 0px 2px 2px 0px, rgba(0, 0, 0, 0.1) 0px 2px 13px 0px"
              opacity={sendEmail ? '1' : '0.45'}
              mentionValues={[]}
              readonly={!sendEmail}
            />
          </div>
          <div>
            <Checkbox
              checked={this.state.scheduleSend}
              onChange={this.handleScheduleSendCheck}
              label="Schedule Send"
              disabled={!sendEmail}
            />
            {this.state.scheduleSend && (
              <Input
                type="datetime-local"
                value={this.state.scheduleSendDateTime}
                onChange={e => this.setState({ scheduleSendDateTime: e.target.value })}
                style={{ marginLeft: 15 }}
                disabled={!sendEmail}
                min={this.state.scheduleSendMin}
              />
            )}
          </div>
          <div
            css={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: 20,
            }}
          >
            <div>
              <div css={{ fontSize: 16 }}>Documents</div>
              {selectedDocuments.map(d => {
                return (
                  <div key={d.id} style={{ display: 'flex', gap: 8 }}>
                    <div css={documentStyles}>
                      <span
                        onClick={() =>
                          d.documentLink && this.openFormClick(this.props.propertyId, d.documentLink)
                        }
                        style={{ cursor: 'pointer' }}
                      >
                        {d.documentType !== null ? d.documentType : 'Order Correspondence'}
                      </span>
                    </div>
                    <Icon
                      style={{ marginTop: 16, cursor: 'pointer' }}
                      name="close"
                      onClick={() => this.props.removeDocument(d.id)}
                    />
                  </div>
                );
              })}
            </div>
            <div />
          </div>
          <div>
            {error && <div css={{ color: colors.red }}>Please select contacts</div>}
            {subjectError && <div css={{ color: colors.red }}>Subject cannot be empty</div>}
            {judicialContinError && (
              <div css={{ color: colors.red }}>Please select judicial contin receivers</div>
            )}
          </div>
          <div css={{ marginTop: 16 }}>
            <div css={{ display: 'flex', justifyContent: 'flex-end' }}>
              {this.state.trackerProError && (
                <div css={{ color: 'red' }}>
                  There was an issue with some of the files we sent to Tracker Pro. Please check notes section
                  for more details!!
                </div>
              )}
            </div>
            <div css={{ display: 'flex', justifyContent: 'flex-end' }}>
              {this.state.riversideUploadmessage && <div>{this.state.riversideUploadmessage}</div>}
            </div>
            <div css={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
              <Button secondary onClick={onClose} disabled={sendStatus}>
                Cancel
              </Button>
              <Button
                loadingStatus={this.props.previewLoading}
                overRidestyle={{ marginLeft: 10 }}
                onClick={() => this.props.handlePreviewClick(this.props.selectedDocuments, this.state.client)}
              >
                Preview
              </Button>
              {this.state.trackerProError != null ? (
                <Button
                  overRidestyle={{ marginLeft: 10 }}
                  onClick={this.props.onClose}
                  disabled={sendStatus}
                  loadingStatus={sendStatus}
                >
                  OK
                </Button>
              ) : (
                <Button
                  overRidestyle={{ marginLeft: 10 }}
                  onClick={this.handleSendClick}
                  disabled={
                    sendStatus || ['checking-size', 'compressing'].includes(this.state.compressionFlow.phase)
                  }
                  loadingStatus={sendStatus || this.state.compressionFlow.phase === 'checking-size'}
                >
                  {this.state.compressionFlow.phase === 'checking-size' ? 'Checking Files…' : 'Send'}
                </Button>
              )}
            </div>
          </div>
        </CustomModal>
        {this.state.sendGridError && (
          <SendGridErrorModal
            sendGridError={this.state.sendGridError}
            setSendGridError={this.handleSetSendGridError}
            errorMessage={this.state.modalErrorMessage}
            possibleAttachmentSizeFailure={this.state.possibleAttachmentSizeFailure}
            canTryCompression={this.state.canTryCompressionAfterFailure}
            multipleAttachments={this.props.selectedDocuments.length > 1}
            onClose={this.props.onClose}
            onDownload={this.handleDownloadFailedEmail}
            onCompress={this.handleCompressAfterFailure}
          />
        )}
        {this.state.compressionFlow.phase === 'prompt' && (
          <LargePdfCompressionPromptModal
            open={this.state.compressionFlow.phase === 'prompt'}
            onClose={this.handleCompressionPromptClose}
            onCompress={this.handleCompressionPromptTry}
            onContinueWithoutCompressing={this.handleCompressionPromptSendOriginal}
          />
        )}
        <PdfCompressionReviewModal
          open={this.state.compressionFlow.phase === 'review'}
          previews={this.state.compressionFlow.previews}
          onClose={this.handleCompressionClose}
          onSendOriginal={this.handleCompressionSendOriginal}
          onConfirm={this.handleCompressionConfirm}
          sendOriginalButtonText={
            this.state.compressionFlow.afterFailure ? 'Return' : 'Continue Without Compressing'
          }
          confirmButtonText={
            this.state.compressionFlow.afterFailure ? 'Save Compressed & Send' : 'Save Compressed & Continue'
          }
        />
        {this.state.integrationAddressWarningOpen && (
          <ConfirmActionModal
            openState={this.state.integrationAddressWarningOpen}
            onClose={this.handleIntegrationAddressWarningClose}
            header="Double Check Email"
            info={getIntegrationAddressWarningMessage(this.state.integrationAddressWarningEmail)}
            actionFunction={this.handleIntegrationAddressWarningContinue}
            buttonText="Continue"
            cancelButtonText="Go Back"
            headerSymbol="!"
            closeOnDimmerClick={false}
            height={330}
          />
        )}
      </React.Fragment>
    );
  }
}
